/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Nikolay Sviridov
 * @version 1.33.0
 * @since 1.33.0
 */

import { Fragment, useCallback, useEffect } from 'react';

import { connect, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { ILogBusinessEventPayload } from '@/main/modules/logging/loggingTypes';
import Title from '@components/common/Title/Title';
import { EBasketType } from '@modules/app/appConstants';
import withAsyncStateManagement from '@modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectTranslationWizardImportSubscribersLimits } from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { IWizardImportSubscribersLimitsTranslation } from '@modules/internationalization/types/wizards/importSubscribersLimits';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import Wizard from '@modules/wizard/components/Wizard';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { IWizardState } from '@modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { IRootReducer } from '@reducers/rootReducer';

import { wizardImportSubscribersLimitsHandleNextAction } from '../wizardImportSubscribersLimitsActions';
import { EWizardImportSubscribersLimitsSteps } from '../wizardImportSubscribersLimitsConstant';
import wizardImportSubscribersLimitsReducer, { IWizardImportSubscribersLimitsReducer } from '../wizardImportSubscribersLimitsReducer';
import { makeSelectWizardImportSubscribersLimitsFileIsLoading, makeSelectWizardImportSubscribersLimitsValidation } from '../wizardImportSubscribersLimitsSelectors';
import WizardImportSubscribersLimitsConfirmation from './WizardImportSubscribersLimitsStepConfirm';
import WizardImportSubscribersLimitsDocumentSelectionStep from './WizardImportSubscribersLimitsStepSelection';
import WizardImportSubscribersLimitsTitleElements from './WizardImportSubscribersLimitsTitleElements';

const WizardImportSubscribersLimits = (props: IConnectedProps & IDispatchProps) => {

    const {
        stepName,
        translation,
        validation,
        fileIsLoading,
        handleNext,
    } = props;

    const dispatch = useDispatch();

    useEffect(() => {
        switch (stepName) {
            case EWizardImportSubscribersLimitsSteps.SelectionStep:
                dispatch(logBusinessEvent({
                    eventName: ELogBusinessEventName.B2B_IMPORT_LIMIT_MASTER_STEP_1,
                    type: ELogBusinessEventType.common,
                }));
                break;
            case EWizardImportSubscribersLimitsSteps.ConfirmStep:
                dispatch(logBusinessEvent({
                    eventName: ELogBusinessEventName.B2B_IMPORT_LIMIT_MASTER_STEP_2,
                    type: ELogBusinessEventType.common,
                }));
                break;
        }
    },[dispatch, stepName]);

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardImportSubscribersLimitsSteps.SelectionStep:
                return <WizardImportSubscribersLimitsDocumentSelectionStep/>;
            case EWizardImportSubscribersLimitsSteps.ConfirmStep:
                return <WizardImportSubscribersLimitsConfirmation/>;
        }
    }, [stepName]);

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.wizardImportSubscribersLimits,
                            TranslationFileNamesEnum.wizard,
                        ]}/>

            <Title pageName={ECommonTranslationPages.wizardImportSubscribersLimits}/>

            <Wizard
                basketType={EBasketType.MOBILE}
                isNextDisabled={!validation?.isValid || fileIsLoading}
                name={EWizardName.IMPORT_SUBSCRIBERS_LIMITS}
                titleElements={
                    <WizardImportSubscribersLimitsTitleElements
                        title={translation.LimitsConfirmImportStep.exportButton}
                    />
                }
                translationProps={{
                    error: translation?.error,
                    steps: translation?.steps,
                    title: translation?.title,
                }}
                validateError={validation?.error}
                onWizardNextHandler={handleNext}
            >
                {renderStep()}
            </Wizard>

        </Fragment>
    );
};

interface IConnectedProps extends Pick<IWizardState, 'stepName'>, Pick<IWizardImportSubscribersLimitsReducer, 
'validation' |
'fileIsLoading'
> {
    translation: IWizardImportSubscribersLimitsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    stepName: makeSelectWizardStepName(),
    translation: makeSelectTranslationWizardImportSubscribersLimits(),
    validation: makeSelectWizardImportSubscribersLimitsValidation(),
    fileIsLoading: makeSelectWizardImportSubscribersLimitsFileIsLoading(),
});

export interface IDispatchProps {
    handleNext: () => void;
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(wizardImportSubscribersLimitsHandleNextAction()),
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(WizardImportSubscribersLimits),
    { reducer: { wizardImportSubscribersLimits: wizardImportSubscribersLimitsReducer } },
);
